import { hotkeysCoreFeature, syncDataLoaderFeature } from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import { Link, useRouterState } from '@tanstack/react-router';
import { PanelLeftClose, PanelRightClose } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { NavSearchCommand } from '@/components/NavSearchCommand';
import { Tree, TreeItem, TreeItemLabel } from '@/components/reui/tree';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  buildNavItems,
  getExpandedIdsForPath,
  getNavEmoji,
  getNavSearchItems,
  type NavItem,
} from '@/config/navTree';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { isAdmin, normalizeRole } from '@/utils/roles';

const indent = 5;
const COLLAPSED_STORAGE_KEY = 'nav-sidebar-collapsed';

export function AppNavSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user } = useAuth();
  const userIsAdmin = isAdmin(normalizeRole(user?.role));
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true'
  );

  const { items, rootItemId } = useMemo(() => buildNavItems(userIsAdmin), [userIsAdmin]);
  const searchItems = useMemo(() => getNavSearchItems(items, rootItemId), [items, rootItemId]);

  const [expandedItems, setExpandedItems] = useState<string[]>(() =>
    getExpandedIdsForPath(pathname, items)
  );

  useEffect(() => {
    const needed = getExpandedIdsForPath(pathname, items);
    setExpandedItems((prev) => [...new Set([...prev, ...needed])]);
  }, [pathname, items]);

  useEffect(() => {
    if (collapsed) {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(COLLAPSED_STORAGE_KEY);
    }
  }, [collapsed]);

  const tree = useTree<NavItem>({
    rootItemId,
    indent,
    state: { expandedItems },
    setState: (updater) => {
      const next = typeof updater === 'function' ? updater({ expandedItems }) : updater;
      if (next.expandedItems) {
        setExpandedItems(next.expandedItems);
      }
    },
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => (item.getItemData()?.children?.length ?? 0) > 0,
    dataLoader: {
      getItem: (itemId) => items[itemId],
      getChildren: (itemId) => items[itemId]?.children ?? [],
    },
    features: [syncDataLoaderFeature, hotkeysCoreFeature],
  });

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col border-r border-border-default bg-white py-3 transition-[width]',
        collapsed ? 'w-12' : 'w-[190px]'
      )}
      aria-label="Application navigation"
    >
      <div className="mr-1 flex shrink-0 justify-end px-1.5 pb-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="bg-white"
          aria-expanded={!collapsed}
          aria-label="Toggle navigation sidebar"
          onClick={() => setCollapsed((prev) => !prev)}
        >
          {collapsed ? <PanelRightClose /> : <PanelLeftClose />}
        </Button>
      </div>

      {collapsed ? (
        <nav className="flex min-h-0 flex-1 flex-col items-center gap-0.5 overflow-y-auto px-1">
          {searchItems.map((item) => {
            const isActive = pathname === item.path;
            return (
              <Tooltip key={item.id}>
                <TooltipTrigger
                  render={
                    <Link
                      to={item.path}
                      aria-label={item.name}
                      className={cn(
                        'flex size-8 items-center justify-center rounded-sm text-base no-underline transition-colors hover:bg-accent hover:text-accent-foreground hover:shadow-sm',
                        isActive && 'bg-accent font-semibold text-accent-foreground'
                      )}
                    />
                  }
                >
                  {getNavEmoji(item.name)}
                </TooltipTrigger>
                <TooltipContent side="right">{item.name}</TooltipContent>
              </Tooltip>
            );
          })}
        </nav>
      ) : (
        <>
          <div className="shrink-0 px-2 pb-2">
            <NavSearchCommand />
          </div>
          <nav className="min-h-0 flex-1 overflow-y-auto px-2">
            <Tree indent={indent} tree={tree} className="w-full">
              {tree.getItems().map((item) => {
                if (item.getId() === rootItemId) return null;

                const data = item.getItemData();
                const path = data.path;
                const isActive = path === pathname;

                if (path) {
                  return (
                    <TreeItem
                      key={item.getId()}
                      item={item}
                      render={
                        <Link
                          to={path}
                          className="block w-full border-0 bg-transparent p-0 text-left no-underline"
                        />
                      }
                    >
                      <TreeItemLabel
                        className={cn(isActive && 'bg-accent font-semibold text-accent-foreground')}
                      />
                    </TreeItem>
                  );
                }

                return (
                  <TreeItem key={item.getId()} item={item}>
                    <TreeItemLabel />
                  </TreeItem>
                );
              })}
            </Tree>
          </nav>
        </>
      )}
    </aside>
  );
}
